/*
  <bb-reviews> — shared controller for the native Trustpilot review components.

  Drives every review placement:
    - snippets/bb-service-reviews.liquid  -> /apps/reviews/service-reviews
        (on a product page: /apps/reviews/product-reviews?sku=... for that
        product, same URL snippet as below)
        rendered by the "BB Trustpilot reviews" section and by its block in
        the cart order summary, which passes layout 'single': one review at a
        time (.bb-reviews--single), browsed client-side from the one response.
        one carousel everywhere it is placed: items_desktop /
        items_tablet / items_mobile cards at a time out of reviews_limit, with
        the arrows, dots, rotation and looping its "Carousel controls" group
        configures. A one-card, auto-rotating placement is just that carousel
        with the item counts set to 1.
    - snippets/bb-product-reviews.liquid  -> /apps/reviews/product-reviews?sku=...
        the one product-review renderer, shared by the Product Information
        "BB Product Reviews" block and the Home Banner GRID right column. Its
        summary and carousel are independently optional, so either half may be
        missing from the markup — everything below treats both as optional.
        With display_style "compact" it renders only the PDP rating row
        (stars from data.stars, "{count} reviews" from data.total, a caret).
    - sections/bb-product-reviews-full.liquid -> same product-reviews URL
        the PDP's full paginated list, <bb-reviews-full> in
        assets/bb-reviews-full.js, which extends this element.

  Response envelope (identical on both routes):
    { success: true,
      data: { stars, trustScore, total, profileUrl, hasMore, reviews: [...] },
      message: null }

  Reviews live at response.data.reviews — never at the root. The aggregate
  (data.stars / data.trustScore / data.total) is the source of truth and is never
  recomputed from the returned items.

  Behaviour:
    - ONE request per endpoint, fired only as the first placement using it
      approaches the viewport. Placements that build the identical endpoint (the
      PDP compact row and the carousel lower down) share that one response, and
      every card and every slide change is served from it.
    - no client-side persistence: the app proxy already caches (30 min fresh,
      up to 7 days stale) so a second cache layer here would only serve staler data
    - an empty payload (success !== true / no data / no reviews) or a failed
      request removes the section entirely — no placeholder, no reserved space
    - the carousel is slick (already loaded theme-wide); with slick unavailable the
      list degrades to the CSS scroll-snap row it is styled as by default
    - with data-text-limit set, long review text is previewed word-aware in the
      card and a "read more" button opens the FULL text in one modal per
      instance — the theme's own <modal-dialog>, filled from the review already
      in memory. Cards never expand, so the carousel keeps equal heights.
    - with data-review-display-type="tooltip" (the section's "Review display")
      that same "read more" shows the full review in one tooltip per instance
      instead: hover or keyboard focus on desktop, a tap on touch screens. Same
      review object, same content builder — only the presentation differs.
    - the summary row's two halves (rating data, Trustpilot attribution) are
      each optional in the markup (snippets/bb-reviews-summary.liquid). When
      the rating is rendered as a scroll button (the PDP block), clicking it
      scrolls to the wrapper it names by id, clear of the sticky header.
*/

(function () {
  'use strict';

  /*
    Each placement prints this script tag. Only the first copy may define the
    element and own the shared request cache below.
  */
  if (window.customElements.get('bb-reviews')) return;

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var XLINK_NS = 'http://www.w3.org/1999/xlink';
  /* Matches the square, padded viewBox in snippets/bb-review-star-defs.liquid. */
  var STAR_VIEWBOX_WIDTH = 240;
  var STAR_VIEWBOX_HEIGHT = 240;
  var MAX_STARS = 5;
  /* px, the star's size before any stylesheet applies. */
  var STAR_INTRINSIC_SIZE = '14';

  /*
    Trustpilot's own wording for a rounded star rating. Keyed off data.stars,
    which the API already rounds to the nearest half star.
  */
  var RATING_WORDS = [
    [4.5, 'excellent'],
    [3.5, 'great'],
    [2.5, 'average'],
    [1.5, 'poor'],
    [0, 'bad']
  ];

  /* Largest unit first: the first one the age reaches is the one we report. */
  var RELATIVE_UNITS = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60]
  ];

  var clipIdCounter = 0;

  /*
    A hung request must not leave the skeleton up forever. The app proxy serves
    from cache (30 min fresh, 7 days stale), so anything this slow is broken.
  */
  var REQUEST_TIMEOUT_MS = 10000;

  /* sections/bb-service-reviews.liquid's wrapper id on a product page. */
  var PRODUCT_PAGE_REVIEWS_ID = 'bb-reviews--product-page';

  /* sections/bb-product-reviews-full.liquid's wrapper. */
  var FULL_SECTION_SELECTOR = '[data-bb-reviews-full-section]';

  /*
    Requests by endpoint, in flight or settled, so every placement asking for the
    same endpoint shares one fetch. A failure is dropped so a later placement
    may try again.
  */
  var sharedRequests = {};

  /*
    snippets/bb-review-endpoint.liquid's marker for a product whose colours are
    separate products sharing a SKU-group tag. Never sent to the API.
  */
  var SKU_GROUP_PARAM = 'sku_group';

  /*
    Shorter than REQUEST_TIMEOUT_MS so a slow lookup still leaves time for the
    reviews request itself, on the product's own SKUs.
  */
  var GROUP_TIMEOUT_MS = 4000;

  /* Group lookups by group, shared like the reviews requests; failures are dropped. */
  var groupSkuRequests = {};

  /* The API answers a request naming more SKUs than this with a 422. */
  var MAX_SKUS_PER_REQUEST = 50;

  /*
    Most requests one endpoint page may cost. The largest SKU group in the
    catalogue has 102 SKUs (3 batches); SKUs past this many batches are dropped
    and the console says so.
  */
  var MAX_SKU_BATCHES = 4;

  /*
    The last page of a batch (data.hasMore false), with its aggregate, by the
    batch's URL minus `page`. "Load more" past that page reuses the aggregate
    instead of asking the API for an empty page.
  */
  var exhaustedBatches = {};

  /*
    Every variant SKU of every product tagged with the group, from
    templates/collection.bb-review-skus.liquid via the storefront's tag filter.
  */
  function requestGroupSkus(group) {
    if (groupSkuRequests[group]) return groupSkuRequests[group];

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'collections/all/' + encodeURIComponent(group) + '?view=bb-review-skus';
    /*
      No `Accept: application/json`: Shopify reads it as a request for the
      collection's JSON format and answers a tag-filtered ?view= URL with 404.
      The view prints JSON anyway.
    */
    var options = {};
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) options.signal = controller.signal;

    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, GROUP_TIMEOUT_MS);

    var request = fetch(url, options)
      .then(function (response) {
        if (!response.ok) throw new Error('SKU group request failed: ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        clearTimeout(timer);
        if (!payload || !Array.isArray(payload.skus)) throw new Error('SKU group response malformed');
        return payload.skus;
      })
      .catch(function (error) {
        clearTimeout(timer);
        delete groupSkuRequests[group];
        throw error;
      });

    groupSkuRequests[group] = request;
    return request;
  }

  function warn(message, detail) {
    if (window.console && console.warn) console.warn('[bb-reviews] ' + message, detail === undefined ? '' : detail);
  }

  /*
    The endpoint's `sku` values in order, split on the literal commas before
    decoding so a comma inside a SKU (printed as %2C) survives.
  */
  function skusOf(query) {
    var skus = [];
    query.split('&').forEach(function (pair) {
      var eq = pair.indexOf('=');
      if (eq === -1 || pair.slice(0, eq) !== 'sku') return;
      pair
        .slice(eq + 1)
        .split(',')
        .forEach(function (sku) {
          try {
            skus.push(decodeURIComponent(sku.replace(/\+/g, ' ')));
          } catch (error) {
            skus.push(sku);
          }
        });
    });
    return skus;
  }

  /*
    The requests actually made for an endpoint, as { url, key, page }: one
    unless there are more than MAX_SKUS_PER_REQUEST SKUs.

    With a sku_group marker, `sku` becomes the endpoint's own SKUs (the viewed
    product's variants, in variant order) followed by the group's (in
    collection and variant order), trimmed and de-duplicated with the first
    occurrence kept. Never sorted: the viewed product's SKUs must lead, so they
    are always in the first batch. Should the lookup fail, the endpoint's own
    SKUs are sent and the console says so.

    The list is then cut into batches of at most MAX_SKUS_PER_REQUEST, each
    carrying every other parameter unchanged. Each SKU is encoded on its own and
    the commas stay literal, as bb-review-skus prints them. An endpoint without
    the marker and within the limit is fetched exactly as printed.
  */
  function resolveEndpoint(endpoint) {
    var queryAt = endpoint.indexOf('?');
    if (queryAt === -1 || typeof URLSearchParams !== 'function') return Promise.resolve([{ url: endpoint }]);

    var query = endpoint.slice(queryAt + 1);
    var params = new URLSearchParams(query);
    var group = params.get(SKU_GROUP_PARAM);
    var ownSkus = skusOf(query);

    if (!group && ownSkus.length <= MAX_SKUS_PER_REQUEST) return Promise.resolve([{ url: endpoint }]);

    var skus = !group
      ? Promise.resolve(ownSkus)
      : requestGroupSkus(group).then(
          function (groupSkus) {
            return ownSkus.concat(groupSkus);
          },
          function (error) {
            warn('SKU group "' + group + '" could not be loaded; requesting this product\'s SKUs only.', error.message || error);
            return ownSkus;
          }
        );

    return skus.then(function (skus) {
      var seen = {};
      var unique = [];
      skus.forEach(function (sku) {
        sku = String(sku == null ? '' : sku).trim();
        if (sku && !seen.hasOwnProperty(sku)) {
          seen[sku] = true;
          unique.push(sku);
        }
      });

      var batches = [];
      for (var i = 0; i < unique.length; i += MAX_SKUS_PER_REQUEST) {
        batches.push(unique.slice(i, i + MAX_SKUS_PER_REQUEST));
      }
      if (batches.length > MAX_SKU_BATCHES) {
        warn(
          unique.length + ' SKUs exceed ' + MAX_SKU_BATCHES + ' requests of ' + MAX_SKUS_PER_REQUEST + '; reviews of the last ' +
            (unique.length - MAX_SKU_BATCHES * MAX_SKUS_PER_REQUEST) + ' are not requested.'
        );
        batches.length = MAX_SKU_BATCHES;
      }
      if (!batches.length) return [{ url: endpoint }];

      var rest = '';
      var restWithoutPage = '';
      params.forEach(function (value, key) {
        if (key === 'sku' || key === SKU_GROUP_PARAM) return;
        var pair = '&' + encodeURIComponent(key) + '=' + encodeURIComponent(value);
        rest += pair;
        if (key !== 'page') restWithoutPage += pair;
      });

      var page = Number(params.get('page')) || 1;
      return batches.map(function (batch) {
        var base = endpoint.slice(0, queryAt) + '?sku=' + batch.map(encodeURIComponent).join(',');
        return { url: base + rest, key: base + restWithoutPage, page: page };
      });
    });
  }

  function fetchJson(url, options) {
    return fetch(url, options).then(function (response) {
      if (!response.ok) throw new Error('Reviews request failed: ' + response.status);
      return response.json();
    });
  }

  /* One batch's page as { data } for a usable response, { error } otherwise. */
  function requestBatch(batch, options) {
    var known = exhaustedBatches[batch.key];
    if (known && batch.page > known.page) {
      return Promise.resolve({ data: Object.assign({}, known.data, { hasMore: false, reviews: [] }) });
    }

    return fetchJson(batch.url, options).then(
      function (payload) {
        var data = payload && payload.data;
        if (!payload || payload.success !== true || !data || !Array.isArray(data.reviews)) {
          return { error: new Error('Reviews response unsuccessful or malformed') };
        }
        if (data.hasMore !== true) exhaustedBatches[batch.key] = { page: batch.page, data: data };
        return { data: data };
      },
      function (error) {
        return { error: error };
      }
    );
  }

  function newestFirst(a, b) {
    return (Date.parse(b && b.createdAt) || 0) - (Date.parse(a && a.createdAt) || 0);
  }

  /*
    One response envelope from the batches that answered. A batch's SKUs are
    disjoint from every other's and each review belongs to one SKU, so the
    aggregates add up: total and distribution are summed, stars is the mean of
    the summed distribution (rounded to the half star, as the API rounds it),
    trustScore the total-weighted mean of those that have one. Reviews are
    de-duplicated by id regardless and put newest first; hasMore holds while
    any batch has another page.
  */
  function mergeBatches(results) {
    var answered = results.filter(function (result) {
      return result.data;
    });
    if (!answered.length) throw results[0].error;
    if (answered.length < results.length) {
      warn(
        results.length - answered.length + ' of ' + results.length + ' review requests failed; showing the rest.',
        results
          .filter(function (result) {
            return result.error;
          })
          .map(function (result) {
            return result.error.message || result.error;
          })
      );
    }

    var seen = {};
    var reviews = [];
    var total = 0;
    var distribution = {};
    var weightedStars = 0;
    var scoreSum = 0;
    var scoreWeight = 0;
    var hasMore = false;
    var profileUrl = null;
    var hasDistribution = false;

    answered.forEach(function (result) {
      var data = result.data;
      var count = Number(data.total) || 0;
      total += count;
      weightedStars += (Number(data.stars) || 0) * count;
      if (data.trustScore != null && count) {
        scoreSum += Number(data.trustScore) * count;
        scoreWeight += count;
      }
      if (data.distribution) {
        hasDistribution = true;
        Object.keys(data.distribution).forEach(function (rating) {
          distribution[rating] = (distribution[rating] || 0) + (Number(data.distribution[rating]) || 0);
        });
      }
      hasMore = hasMore || data.hasMore === true;
      profileUrl = profileUrl || data.profileUrl || null;

      data.reviews.forEach(function (review) {
        if (review && review.id) {
          if (seen[review.id]) return;
          seen[review.id] = true;
        }
        reviews.push(review);
      });
    });

    reviews.sort(newestFirst);

    var rated = 0;
    var ratingSum = 0;
    Object.keys(distribution).forEach(function (rating) {
      rated += distribution[rating];
      ratingSum += Number(rating) * distribution[rating];
    });
    var mean = rated ? ratingSum / rated : total ? weightedStars / total : 0;

    return {
      success: true,
      data: Object.assign({}, answered[0].data, {
        stars: Math.round(mean * 2) / 2,
        trustScore: scoreWeight ? Math.round((scoreSum / scoreWeight) * 10) / 10 : answered[0].data.trustScore,
        total: total,
        distribution: hasDistribution ? distribution : answered[0].data.distribution,
        hasMore: hasMore,
        profileUrl: profileUrl,
        reviews: reviews
      }),
      message: null,
      partial: answered.length < results.length
    };
  }

  function requestReviews(endpoint) {
    if (sharedRequests[endpoint]) return sharedRequests[endpoint];

    var options = { headers: { Accept: 'application/json' } };
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) options.signal = controller.signal;

    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, REQUEST_TIMEOUT_MS);

    var request = resolveEndpoint(endpoint)
      .then(function (batches) {
        if (batches.length === 1) return fetchJson(batches[0].url, options);
        return Promise.all(
          batches.map(function (batch) {
            return requestBatch(batch, options);
          })
        ).then(mergeBatches);
      })
      .then(
        function (payload) {
          clearTimeout(timer);
          /* A partial merge serves the placements already waiting; a later one tries again. */
          if (payload && payload.partial) delete sharedRequests[endpoint];
          return payload;
        },
        function (error) {
          clearTimeout(timer);
          delete sharedRequests[endpoint];
          throw error;
        }
      );

    sharedRequests[endpoint] = request;
    return request;
  }

  /*
    Review tooltip geometry, in px: the gap leaves room for the arrow, the margin
    keeps the box off every viewport edge, and the inset keeps the arrow off the
    box's corners when the box is pushed in from an edge.
  */
  var TOOLTIP_GAP = 10;
  var TOOLTIP_MARGIN = 16;
  var TOOLTIP_MIN_HEIGHT = 80;
  var TOOLTIP_ARROW_INSET = 14;

  /* Long enough for the pointer to cross the gap from the trigger onto the tooltip. */
  var TOOLTIP_HIDE_DELAY_MS = 120;

  /* Past this much movement a press is a carousel drag, not a tap or a click. */
  var DRAG_THRESHOLD = 8;

  function svgEl(name) {
    return document.createElementNS(SVG_NS, name);
  }

  /* Modern browsers use `href`; xlink:href keeps older Safari happy. */
  function setHref(node, value) {
    node.setAttribute('href', value);
    node.setAttributeNS(XLINK_NS, 'xlink:href', value);
  }

  function fill(template, values) {
    if (!template) return '';
    return template.replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  /*
    Word-aware preview of a review. Returns null when the text already fits, so
    callers can tell "not truncated" apart from a preview; the source string is
    never modified. Counts code points rather than UTF-16 units, so an emoji is
    never split in half.
  */
  function truncateText(text, limit) {
    var source = String(text || '').trim();
    var chars = Array.from(source);
    if (!limit || chars.length <= limit) return null;

    var cut = chars.slice(0, limit).join('');

    /*
      Back up to the last word boundary unless the cut already ends on one. One
      very long "word" (a URL, say) would lose too much, so past a fifth of the
      allowance the cut stays where it is.
    */
    if (!/\s/.test(chars[limit])) {
      var boundary = cut.search(/\s\S*$/);
      if (boundary > cut.length * 0.8) cut = cut.slice(0, boundary);
    }

    /* "process." + "…" reads badly: drop trailing spaces and punctuation first. */
    var trimmed = cut.replace(/[\s.,;:!?\-\u2013\u2014\u2026'"\u00ab(]+$/, '');

    return (trimmed || cut) + '\u2026';
  }

  function jq() {
    var $ = window.jQuery || window.$;
    return $ && $.fn && $.fn.slick ? $ : null;
  }

  function prefersReducedMotion() {
    return false;
    // return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function stickyHeaderOffset() {
    var header = document.querySelector('sticky-header');
    return header ? header.getBoundingClientRect().height : 0;
  }

  /* Animated in JS: browsers turn `behavior: 'smooth'` into a jump when the OS disables animations. */
  function smoothScrollTo(top, duration) {
    var start = window.pageYOffset;
    var distance = top - start;
    if (!distance) return;

    var startTime = null;
    var cancelled = false;
    var cancel = function () { cancelled = true; };
    window.addEventListener('wheel', cancel, { passive: true, once: true });
    window.addEventListener('touchstart', cancel, { passive: true, once: true });

    function step(now) {
      if (cancelled) return;
      if (startTime === null) startTime = now;
      var t = Math.min(1, (now - startTime) / duration);
      var eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      window.scrollTo(0, start + distance * eased);
      if (t < 1) {
        window.requestAnimationFrame(step);
      } else {
        window.removeEventListener('wheel', cancel);
        window.removeEventListener('touchstart', cancel);
      }
    }
    window.requestAnimationFrame(step);
  }

  function buildStars(rating, symbolId, label) {
    var wrapper = document.createElement('span');
    wrapper.className = 'bb-stars';
    wrapper.setAttribute('role', 'img');
    wrapper.setAttribute('aria-label', label);

    var value = Math.max(0, Math.min(MAX_STARS, Number(rating) || 0));

    for (var i = 0; i < MAX_STARS; i++) {
      var portion = Math.max(0, Math.min(1, value - i));
      var star = svgEl('svg');
      star.setAttribute('class', 'bb-stars__star');
      star.setAttribute('viewBox', '0 0 ' + STAR_VIEWBOX_WIDTH + ' ' + STAR_VIEWBOX_HEIGHT);
      /*
        Intrinsic size: an SVG with only a viewBox stretches to its container's
        width. .bb-stars__star (--bb-star-size) overrides these attributes.
      */
      star.setAttribute('width', STAR_INTRINSIC_SIZE);
      star.setAttribute('height', STAR_INTRINSIC_SIZE);
      star.setAttribute('aria-hidden', 'true');
      star.setAttribute('focusable', 'false');

      /*
        Anything not fully filled gets the outlined star underneath, so a half
        star reads as an outline with its left half filled in.
      */
      if (portion < 1) {
        var empty = svgEl('use');
        setHref(empty, '#' + symbolId);
        empty.setAttribute('class', 'bb-stars__star-empty');
        empty.setAttribute('width', '100%');
        empty.setAttribute('height', '100%');
        star.appendChild(empty);
      }

      if (portion > 0) {
        var full = svgEl('use');
        setHref(full, '#' + symbolId);
        full.setAttribute('class', 'bb-stars__star-full');
        full.setAttribute('width', '100%');
        full.setAttribute('height', '100%');

        if (portion < 1) {
          clipIdCounter += 1;
          var clipId = 'bb-star-clip-' + clipIdCounter;

          var defs = svgEl('defs');
          var clipPath = svgEl('clipPath');
          clipPath.setAttribute('id', clipId);

          var rect = svgEl('rect');
          rect.setAttribute('x', '0');
          rect.setAttribute('y', '0');
          rect.setAttribute('width', String(STAR_VIEWBOX_WIDTH * portion));
          rect.setAttribute('height', String(STAR_VIEWBOX_HEIGHT));
          rect.setAttribute('fill', '#fff');

          clipPath.appendChild(rect);
          defs.appendChild(clipPath);
          star.appendChild(defs);

          full.setAttribute('clip-path', 'url(#' + clipId + ')');
        }

        star.appendChild(full);
      }

      wrapper.appendChild(star);
    }

    return wrapper;
  }

  class BBReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.inner = this.querySelector('[data-bb-reviews-inner]');
      this.summaryEl = this.querySelector('[data-bb-reviews-summary]');
      this.summaryDataEl = this.querySelector('[data-bb-reviews-summary-data]');
      this.listEl = this.querySelector('[data-bb-reviews-list]');
      this.controlsEl = this.querySelector('[data-bb-reviews-controls]');
      this.dotsEl = this.querySelector('[data-bb-reviews-dots]');
      this.toggleEl = this.querySelector('[data-bb-reviews-toggle]');
      this.prevEl = this.querySelector('[data-bb-reviews-prev]');
      this.nextEl = this.querySelector('[data-bb-reviews-next]');
      this.attributionEl = this.querySelector('[data-bb-reviews-attribution]');
      this.skeletonEl = this.querySelector('[data-bb-reviews-skeleton]');
      this.compactEl = this.querySelector('[data-bb-reviews-compact]');

      /* The list is optional: "summary only" renders no carousel at all. */
      if (!this.inner) return;

      this.strings = this.readStrings();
      this.endpoint = this.dataset.endpoint;
      this.symbolId = this.dataset.starSymbol;
      this.locale = this.dataset.locale || undefined;
      this.designMode = this.dataset.designMode === 'true';
      this.isCompact = this.dataset.displayStyle === 'compact' && !!this.compactEl;

      /* How many reviews go INTO the carousel. */
      this.reviewsLimit = parseInt(this.dataset.reviewsLimit, 10) || 10;

      /* How many are visible AT ONCE, per breakpoint. */
      this.itemsDesktop = parseInt(this.dataset.itemsDesktop, 10) || 1;
      this.itemsTablet = parseInt(this.dataset.itemsTablet, 10) || 1;
      this.itemsMobile = parseInt(this.dataset.itemsMobile, 10) || 1;

      /*
        Carousel controls. Every data attribute is a string, so each flag is
        compared explicitly: Boolean('false') would be true.

        `rotating` is the master switch for automatic movement and `autoplay`
        the behaviour it allows, so slick only ever advances on its own when
        both are on. `pauseOnHover` only refines that movement, so it does
        nothing unless autoplay is actually running.
      */
      this.showDots = this.dataset.showDots === 'true';
      this.rotating = this.dataset.rotating === 'true';
      this.autoplay = this.dataset.autoplay === 'true';
      this.infinite = this.dataset.infinite === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;

      /*
        Which halves this instance renders. The markup already omits whichever
        is off; these mirror it so nothing tries to fill an absent element.
      */
      this.showSummary = this.dataset.showSummary !== 'false' && !!this.summaryEl;
      this.showCarousel = this.dataset.showCarousel !== 'false' && !!this.listEl;

      this.showAuthor = this.dataset.showAuthor === 'true';
      this.showVerified = this.dataset.showVerified === 'true';
      this.showScoreDetails = this.dataset.showScoreDetails === 'true';

      /*
        Review text preview. 0 / absent means no limit — the product-review
        placements pass none, so their cards are unchanged.
      */
      this.textLimit = parseInt(this.dataset.textLimit, 10) || 0;
      this.modalTemplate = this.querySelector('[data-bb-reviews-modal-template]');

      /*
        How "read more" presents the full review. Anything but "tooltip" —
        including placements that never pass the attribute — keeps the modal.
      */
      this.reviewDisplayType = this.dataset.reviewDisplayType === 'tooltip' ? 'tooltip' : 'modal';
      this.tooltipTemplate = this.querySelector('[data-bb-reviews-tooltip-template]');

      this.reviews = [];

      this.userPaused = false;

      /*
        Delegated, and bound once per element (connectedCallback is guarded
        above): slick's infinite mode clones slides without their listeners,
        and a clone's "read more" must open the modal too.
      */
      if (this.listEl) this.listEl.addEventListener('click', this.onListClick.bind(this));
      if (this.listEl && this.reviewDisplayType === 'tooltip') this.bindTooltipTriggers();

      /*
        The PDP block's rating — the full summary's or the compact row — is a
        button naming the wrapper it scrolls to. The compact row always listens
        and resolves its target on click (see resolveScrollTarget).
      */
      var scrollTrigger = this.isCompact ? this.compactEl : this.summaryDataEl;
      this.scrollTargetId = scrollTrigger ? scrollTrigger.getAttribute('data-bb-reviews-scroll-target') : null;
      if (this.scrollTargetId || this.isCompact) {
        scrollTrigger.addEventListener('click', this.onSummaryClick.bind(this));
      }

      if (!this.endpoint) {
        this.hide();
        return;
      }

      this.loadWhenNearViewport();
    }

    disconnectedCallback() {
      this.clearWatchdog();
      this.destroyModal();
      this.destroyTooltip();

      var $ = jq();
      if ($ && this.listEl && this.listEl.classList.contains('slick-initialized')) {
        $(this.listEl).slick('unslick');
      }
    }

    readStrings() {
      var node = this.querySelector('[data-bb-reviews-strings]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent) || {};
      } catch (error) {
        return {};
      }
    }

    /*
      Keeps the request off the critical path: it fires once, shortly before the
      section scrolls into view, and never again — scrolling in and out does not
      re-trigger it, and neither does moving between slides.
    */
    loadWhenNearViewport() {
      var load = this.load.bind(this);

      if (typeof window.initLazyScript === 'function') {
        window.initLazyScript(this, load, 400);
      } else if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        observer.observe(this);
      } else {
        load();
      }
    }

    load() {
      if (this.requested) return;
      this.requested = true;

      var self = this;

      /* The shared request aborts itself at the same timeout; this covers a fetch that ignores the signal. */
      this.watchdog = setTimeout(function () {
        self.timedOut = true;
        self.hide();
      }, REQUEST_TIMEOUT_MS);

      requestReviews(this.requestUrl())
        .then(function (payload) {
          if (self.timedOut) return;

          var data = payload && payload.data;
          var reviews = (data && data.reviews) || [];

          if (!payload || payload.success !== true || !data || !Array.isArray(reviews)) {
            self.hide();
            return;
          }

          if (self.isEmpty(data, reviews)) {
            self.hide();
          } else {
            self.render(data, reviews.slice(0, self.reviewsLimit));
          }

          self.toggleWriteReview(reviews.length > 0);
        })
        .catch(function (error) {
          if (self.timedOut) return;
          self.hide();
          /* Surfaced only in the theme editor so the storefront console stays clean. */
          if (self.designMode && window.console && console.warn) {
            console.warn('[bb-reviews]', error.message || error);
          }
        });
    }

    /*
      snippets/bb-product-reviews.liquid renders .bb-reviews__write-review_left
      just before this element's wrapper. With no reviews it takes the inner's
      place; with reviews it goes and the inner comes back. Placements without
      the element (e.g. the full section) are left alone.
    */
    toggleWriteReview(hasReviews) {
      var writeReview = this.findWriteReview();
      if (!writeReview) return;

      writeReview.classList.toggle('hidden', hasReviews);
      this.inner.hidden = !hasReviews;
    }

    findWriteReview() {
      var wrapper = this.closest('.bb-reviews-wrapper, .bb-product-reviews--compact') || this;
      var node = wrapper.previousElementSibling;

      while (node) {
        if (node.classList.contains('bb-reviews__write-review_left')) return node;
        node = node.previousElementSibling;
      }

      return null;
    }

    /* The first request's URL; the full section adds its page to it. */
    requestUrl() {
      return this.endpoint;
    }

    /*
      Whether a successful response has nothing worth showing. The compact row
      shows only the aggregate, so data.total decides it; the carousel and
      summary need review items. Either way `total: 0` hides.
    */
    isEmpty(data, reviews) {
      return this.isCompact ? !(Number(data.total) > 0) : reviews.length === 0;
    }

    /*
      Called on every outcome — success, empty and failure alike — so the
      skeleton can never outlive the request.
    */
    settleLoading() {
      this.clearWatchdog();
      this.classList.remove('bb-reviews--loading');
      this.setAttribute('aria-busy', 'false');
      /* The hidden attribute, not a class, so this holds even if the CSS is slow. */
      if (this.skeletonEl) this.skeletonEl.hidden = true;
    }

    clearWatchdog() {
      if (this.watchdog) {
        clearTimeout(this.watchdog);
        this.watchdog = null;
      }
    }

    hide() {
      this.settleLoading();
      this.classList.add('bb-reviews--empty');

      if (this.designMode) {
        this.classList.remove('bb-reviews--empty');
        this.renderEditorNote();
        return;
      }

      /* The compact wrapper carries the title/price spacing, so it goes too. */
      if (this.isCompact) {
        var wrapper = this.closest('.bb-product-reviews--compact');
        if (wrapper) wrapper.hidden = true;
      }
    }

    renderEditorNote() {
      if (!this.inner) return;
      this.inner.textContent = '';
      var note = document.createElement('p');
      note.className = 'bb-reviews__editor-note';
      note.textContent = this.strings.editor_empty || 'No reviews returned yet — this section is hidden on the storefront.';
      this.inner.appendChild(note);
      this.inner.hidden = false;
    }

    render(data, reviews) {
      this.settleLoading();

      if (this.isCompact) this.renderCompact(data, reviews.length > 0);

      if (this.showSummary) {
        this.renderSummary(data);
        this.renderAttribution(data.profileUrl);
      }

      this.items = [];

      /* Kept as returned: the modal reads the full review from here. */
      this.reviews = reviews;

      if (this.showCarousel) {
        this.listEl.textContent = '';
        var self = this;

        reviews.forEach(function (review, index) {
          self.listEl.appendChild(self.buildReview(review, index));
        });

        this.items = Array.prototype.slice.call(this.listEl.children);
      }

      /* Reveal before slick measures, or every slide computes a width of 0. */
      this.inner.hidden = false;
      this.classList.add('bb-reviews--loaded');

      /*
        A carousel failure must never cost us the reviews: if slick throws, the
        list stays the CSS scroll-snap row it is already styled as.
      */
      try {
        this.initCarousel();
      } catch (error) {
        if (this.designMode && window.console && console.warn) {
          console.warn('[bb-reviews] carousel init failed', error.message || error);
        }
      }
    }

    /*
      The centred summary row, matching the reference:

        excellent   4.5 out of 5   [stars]   [Trustpilot]

      Every value is read straight from the payload — the word comes from
      data.stars, the figure from data.stars, and the optional detail line from
      data.trustScore / data.total. Nothing is averaged locally. The Trustpilot
      link sits in the same row and is rendered in Liquid, so it keeps the
      theme's inline logo snippet.
    */
    renderSummary(data) {
      if (!this.summaryEl) return;

      /* Either half may be switched off; the row shows whichever is present. */
      if (this.summaryDataEl) this.renderSummaryData(data);
      this.summaryEl.hidden = false;
    }

    renderSummaryData(data) {
      this.summaryDataEl.textContent = '';

      var stars = Number(data.stars) || 0;
      var rating = this.formatNumber(stars);

      var word = this.strings['word_' + this.ratingWord(stars)];
      if (word) {
        var wordEl = document.createElement('span');
        wordEl.className = 'bb-reviews__word';
        wordEl.textContent = word;
        this.summaryDataEl.appendChild(wordEl);
      }

      /* "4.5" and "out of 5" are separate so the figure can carry its own weight. */
      var ratingEl = document.createElement('span');
      ratingEl.className = 'bb-reviews__rating';

      var valueEl = document.createElement('span');
      valueEl.className = 'bb-reviews__rating-value';
      valueEl.textContent = rating;
      ratingEl.appendChild(valueEl);

      if (this.strings.out_of) {
        var outOfEl = document.createElement('span');
        outOfEl.className = 'bb-reviews__rating-label';
        outOfEl.textContent = fill(this.strings.out_of, { max: MAX_STARS });
        ratingEl.appendChild(outOfEl);
      }

      this.summaryDataEl.appendChild(ratingEl);

      this.summaryDataEl.appendChild(
        buildStars(stars, this.symbolId, fill(this.strings.rating_label, { rating: rating, max: MAX_STARS }))
      );

      /* TrustScore and the review count, off by default to match the design. */
      if (this.showScoreDetails && this.strings.score_details) {
        var details = document.createElement('span');
        details.className = 'bb-reviews__score-details';
        details.textContent = fill(this.strings.score_details, {
          score: this.formatNumber(data.trustScore),
          count: this.formatNumber(data.total, 0)
        });
        this.summaryDataEl.appendChild(details);
      }

      /* As a scroll button, its name says where it goes, not just the rating. */
      if (this.scrollTargetId && this.strings.see_reviews) {
        this.summaryDataEl.setAttribute(
          'aria-label',
          fill(this.strings.see_reviews, {
            rating: fill(this.strings.rating_label, { rating: rating, max: MAX_STARS })
          })
        );
      }
    }

    ratingWord(stars) {
      for (var i = 0; i < RATING_WORDS.length; i++) {
        if (stars >= RATING_WORDS[i][0]) return RATING_WORDS[i][1];
      }
      return RATING_WORDS[RATING_WORDS.length - 1][1];
    }

    renderAttribution(profileUrl) {
      if (!this.attributionEl) return;
      if (profileUrl) this.attributionEl.setAttribute('href', profileUrl);
      this.attributionEl.hidden = false;
    }

    /*
      The PDP rating row under the title:

        [stars]  112 reviews  [caret]

      Stars come straight from data.stars (partial stars included) and the count
      from data.total — never from the returned items. With no full reviews on
      the page to scroll to, the button becomes plain text and loses its caret.
    */
    renderCompact(data, hasReviewItems) {
      var stars = Number(data.stars) || 0;
      var total = Number(data.total) || 0;
      var rating = this.formatNumber(stars);
      var ratingLabel = fill(this.strings.rating_label, { rating: rating, max: MAX_STARS });
      var countText = fill(total === 1 ? this.strings.review_count_one : this.strings.review_count, {
        count: this.formatNumber(total, 0)
      });

      /* The carousel renders review items, so without any there is nothing to scroll to. */
      if (!hasReviewItems || !this.resolveScrollTarget()) {
        var staticRow = document.createElement('div');
        staticRow.className = this.compactEl.className + ' bb-product-reviews__rating--static';
        while (this.compactEl.firstChild) staticRow.appendChild(this.compactEl.firstChild);
        this.compactEl.parentNode.replaceChild(staticRow, this.compactEl);
        this.compactEl = staticRow;
      } else {
        this.compactEl.setAttribute(
          'aria-label',
          fill(this.strings.see_reviews, { rating: ratingLabel + ', ' + countText })
        );
      }

      var starsSlot = this.compactEl.querySelector('[data-bb-reviews-compact-stars]');
      if (starsSlot) {
        starsSlot.textContent = '';
        starsSlot.appendChild(buildStars(stars, this.symbolId, ratingLabel));
      }

      var countEl = this.compactEl.querySelector('[data-bb-reviews-count]');
      if (countEl) countEl.textContent = countText;
    }

    /*
      Where a rating button scrolls to, first match wins: the full review list
      section, the wrapper the button names (unless that is its own), the
      service-reviews section on a product page. Never one that hid itself.
    */
    resolveScrollTarget() {
      var own = this.closest('.bb-reviews-wrapper, .bb-product-reviews');
      var candidates = [
        document.querySelector(FULL_SECTION_SELECTOR),
        this.scrollTargetId ? document.getElementById(this.scrollTargetId) : null,
        document.getElementById(PRODUCT_PAGE_REVIEWS_ID)
      ];

      for (var i = 0; i < candidates.length; i++) {
        var target = candidates[i];
        if (!target || target === own || target.hidden) continue;

        var host = target.querySelector('bb-reviews');
        if (host && host.classList.contains('bb-reviews--empty')) continue;

        return target;
      }

      return null;
    }

    onSummaryClick() {
      var target = this.resolveScrollTarget();
      if (!target) return;

      var top = target.getBoundingClientRect().top + window.pageYOffset - stickyHeaderOffset();

      smoothScrollTo(Math.max(0, Math.round(top)), 600);

      /* Keyboard focus follows the scroll, unless it is already inside the target. */
      if (!target.contains(document.activeElement)) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }

    buildReview(review, index) {
      var article = document.createElement('article');
      article.className = 'bb-review';
      if (review.id) article.dataset.reviewId = review.id;
      if (review.language) article.dataset.language = review.language;

      var inner = document.createElement('div');
      inner.className = 'bb-review__inner';
      article.appendChild(inner);

      var head = document.createElement('header');
      head.className = 'bb-review__head';

      var stars = Number(review.stars) || 0;
      head.appendChild(
        buildStars(
          stars,
          this.symbolId,
          fill(this.strings.rating_label, { rating: this.formatNumber(stars), max: MAX_STARS })
        )
      );

      var relative = this.formatRelativeDate(review.createdAt);
      if (relative) {
        var time = document.createElement('time');
        time.className = 'bb-review__date';
        time.setAttribute('datetime', review.createdAt);
        /* Absolute date stays available on hover / to assistive tech. */
        time.setAttribute('title', this.formatDate(review.createdAt));
        time.textContent = relative;
        head.appendChild(time);
      }

      inner.appendChild(head);

      if (review.title) {
        var title = document.createElement('div');
        title.className = 'bb-review__title';
        title.textContent = review.title;
        inner.appendChild(title);
      }

      if (review.text) {
        /* Only a preview — review.text itself stays whole for the modal / tooltip. */
        var preview = this.canShowDetails() ? truncateText(review.text, this.textLimit) : null;

        var text = document.createElement('p');
        text.className = 'bb-review__text';
        text.textContent = preview || review.text;
        inner.appendChild(text);

        if (preview) {
          var readMore = document.createElement('button');
          readMore.type = 'button';
          readMore.className = 'bb-review__read-more';
          readMore.setAttribute('data-bb-review-read-more', String(index));
          /* A tooltip is a description, not a popup: it gets aria-describedby while shown. */
          if (this.reviewDisplayType === 'modal') readMore.setAttribute('aria-haspopup', 'dialog');
          readMore.textContent = this.strings.read_more || 'Read more';
          inner.appendChild(readMore);
        }
      }

      /*
        author and verified are always mapped and always present in the markup,
        hidden unless the section enables them — so they can be switched on later
        without touching the API mapping.
      */
      var footer = document.createElement('footer');
      footer.className = 'bb-review__footer';

      var author = document.createElement('span');
      author.className = 'bb-review__author';
      author.textContent = review.author || '';
      author.hidden = !(this.showAuthor && review.author);
      footer.appendChild(author);

      var verified = document.createElement('span');
      verified.className = 'bb-review__verified';
      verified.textContent = this.strings.verified || '';
      verified.hidden = !(this.showVerified && review.verified);
      footer.appendChild(verified);

      footer.hidden = author.hidden && verified.hidden;
      inner.appendChild(footer);

      return article;
    }

    canOpenModal() {
      return this.textLimit > 0 && !!this.modalTemplate && !!window.customElements.get('modal-dialog');
    }

    /* The same rule for whichever presentation "Review display" chose. */
    canShowDetails() {
      if (this.reviewDisplayType === 'tooltip') return this.textLimit > 0 && !!this.tooltipTemplate;
      return this.canOpenModal();
    }

    onListClick(event) {
      var button = event.target.closest ? event.target.closest('[data-bb-review-read-more]') : null;
      if (!button || !this.listEl.contains(button)) return;

      var review = this.reviews[parseInt(button.getAttribute('data-bb-review-read-more'), 10)];
      if (!review) return;

      if (this.reviewDisplayType === 'tooltip') {
        this.onTooltipTriggerClick(event, button);
      } else {
        this.openReview(review, button);
      }
    }

    openReview(review, opener) {
      var modal = this.ensureModal();
      if (!modal || typeof modal.show !== 'function') return;

      this.fillModal(review);
      modal.show(opener);

      /* show() focuses the dialog box; the close button is the better landing spot. */
      if (this.modalClose) this.modalClose.focus();
    }

    ensureModal() {
      if (this.modal) return this.modal;
      if (!this.modalTemplate || !this.modalTemplate.content.firstElementChild) return null;

      var modal = this.modalTemplate.content.firstElementChild.cloneNode(true);
      document.body.appendChild(modal);

      this.modal = modal;
      this.modalDialog = modal.querySelector('[role="dialog"]');
      this.modalBody = modal.querySelector('[data-bb-reviews-modal-body]');
      this.modalClose = modal.querySelector('[data-bb-reviews-modal-close]');

      if (this.modalClose && this.strings.close_review) {
        this.modalClose.setAttribute('aria-label', this.strings.close_review);
      }

      /* <modal-dialog> exposes no events; its [open] attribute is the signal. */
      if (typeof MutationObserver === 'function') {
        this.modalObserver = new MutationObserver(this.onModalToggle.bind(this));
        this.modalObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
      }

      return modal;
    }

    onModalToggle() {
      if (!this.$slider || !this.autoplayRunning) return;

      if (this.modal && this.modal.hasAttribute('open')) {
        this.$slider.slick('slickPause');
      } else if (!this.userPaused) {
        this.$slider.slick('slickPlay');
      }
    }

    fillModal(review) {
      if (!this.modalBody) return;
      this.modalBody.textContent = '';

      var details = this.buildReviewDetails(this.modalBody, review, 'bb-reviews-modal', 'h2');
      if (!this.modalDialog) return;

      if (details.title) {
        details.title.id = this.modal.id + '-title';
        this.modalDialog.removeAttribute('aria-label');
        this.modalDialog.setAttribute('aria-labelledby', details.title.id);
      } else {
        this.modalDialog.removeAttribute('aria-labelledby');
        this.modalDialog.setAttribute('aria-label', details.ratingLabel);
      }
    }

    buildReviewDetails(container, review, block, titleTag) {
      var stars = Number(review.stars) || 0;
      var ratingLabel = fill(this.strings.rating_label, { rating: this.formatNumber(stars), max: MAX_STARS });

      var starsEl = document.createElement('div');
      starsEl.className = block + '__stars';
      starsEl.appendChild(buildStars(stars, this.symbolId, ratingLabel));
      container.appendChild(starsEl);

      var title = null;
      if (review.title) {
        title = document.createElement(titleTag);
        title.className = block + '__title';
        title.textContent = review.title;
        container.appendChild(title);
      }

      if (review.text) {
        var text = document.createElement('p');
        text.className = block + '__text';
        text.textContent = review.text;
        container.appendChild(text);
      }

      var meta = document.createElement('div');
      meta.className = block + '__meta';

      if (this.showAuthor && review.author) {
        var author = document.createElement('span');
        author.className = block + '__author';
        author.textContent = review.author;
        meta.appendChild(author);
      }

      var relative = this.formatRelativeDate(review.createdAt);
      if (relative) {
        var time = document.createElement('time');
        time.className = block + '__date';
        time.setAttribute('datetime', review.createdAt);
        time.setAttribute('title', this.formatDate(review.createdAt));
        time.textContent = relative;
        meta.appendChild(time);
      }

      if (this.showVerified && review.verified && this.strings.verified) {
        var verified = document.createElement('span');
        verified.className = block + '__verified';
        verified.textContent = this.strings.verified;
        meta.appendChild(verified);
      }

      if (meta.childNodes.length) container.appendChild(meta);

      return { title: title, ratingLabel: ratingLabel };
    }

    destroyModal() {
      if (!this.modal) return;

      if (this.modalObserver) this.modalObserver.disconnect();
      if (this.modal.hasAttribute('open') && typeof this.modal.hide === 'function') this.modal.hide();
      if (this.modal.parentNode) this.modal.parentNode.removeChild(this.modal);

      this.modal = null;
      this.modalDialog = null;
      this.modalBody = null;
      this.modalClose = null;
      this.modalObserver = null;
    }
    bindTooltipTriggers() {
      var list = this.listEl;

      this.handleTooltipPointerEnd = this.onTooltipPointerEnd.bind(this);
      this.handleTooltipOutsidePress = this.onTooltipOutsidePress.bind(this);
      this.handleTooltipKeydown = this.onTooltipKeydown.bind(this);
      this.handleTooltipReposition = this.scheduleTooltipPosition.bind(this);

      list.addEventListener('pointerdown', this.onTooltipPointerDown.bind(this));
      list.addEventListener('pointermove', this.onTooltipPointerMove.bind(this));
      list.addEventListener('pointerover', this.onTooltipPointerOver.bind(this));
      list.addEventListener('pointerout', this.onTooltipPointerOut.bind(this));
      list.addEventListener('focusin', this.onTooltipFocusIn.bind(this));
      list.addEventListener('focusout', this.onTooltipFocusOut.bind(this));
    }

    tooltipTriggerFrom(node) {
      var trigger = node && node.closest ? node.closest('[data-bb-review-read-more]') : null;
      return trigger && this.listEl.contains(trigger) ? trigger : null;
    }

    onTooltipPointerDown(event) {
      this.lastPointerType = event.pointerType;
      this.pointerStart = { x: event.clientX, y: event.clientY };
      this.pointerDragged = false;

      window.addEventListener('pointerup', this.handleTooltipPointerEnd, true);
      window.addEventListener('pointercancel', this.handleTooltipPointerEnd, true);
    }

    onTooltipPointerMove(event) {
      if (!this.pointerStart || this.pointerDragged) return;

      var distance = Math.abs(event.clientX - this.pointerStart.x) + Math.abs(event.clientY - this.pointerStart.y);
      if (distance < DRAG_THRESHOLD) return;

      this.pointerDragged = true;
      this.hideTooltip();
    }

    onTooltipPointerEnd() {
      this.pointerStart = null;
      window.removeEventListener('pointerup', this.handleTooltipPointerEnd, true);
      window.removeEventListener('pointercancel', this.handleTooltipPointerEnd, true);
    }

    /* Hover is mouse-only: touch pointers fire pointerover too, and taps toggle instead. */
    onTooltipPointerOver(event) {
      if (event.pointerType !== 'mouse' || this.pointerStart) return;

      var trigger = this.tooltipTriggerFrom(event.target);
      if (trigger) this.showTooltip(trigger, false);
    }

    onTooltipPointerOut(event) {
      if (event.pointerType !== 'mouse') return;

      var trigger = this.tooltipTriggerFrom(event.target);
      if (!trigger || trigger !== this.tooltipTrigger) return;
      if (event.relatedTarget && trigger.contains(event.relatedTarget)) return;

      this.scheduleTooltipHide();
    }

    onTooltipFocusIn(event) {
      var trigger = this.tooltipTriggerFrom(event.target);
      if (trigger) this.showTooltip(trigger, false);
    }

    /* A tap-opened tooltip, or one the pointer is reading, outlives the blur. */
    onTooltipFocusOut(event) {
      var trigger = this.tooltipTriggerFrom(event.target);
      if (!trigger || trigger !== this.tooltipTrigger) return;
      if (this.tooltipPinned || this.tooltipHovered) return;

      this.hideTooltip();
    }

    onTooltipTriggerClick(event, trigger) {
      var fromKeyboard = event.detail === 0;
      if (!fromKeyboard && this.pointerDragged) return;

      var isTouch = !fromKeyboard && !!this.lastPointerType && this.lastPointerType !== 'mouse';

      if (!isTouch) {
        this.showTooltip(trigger, false);
      } else if (this.tooltipPinned && this.tooltipTrigger === trigger) {
        this.hideTooltip();
      } else {
        this.showTooltip(trigger, true);
      }
    }

    onTooltipOutsidePress(event) {
      var target = event.target;
      if (this.tooltip && this.tooltip.contains(target)) return;
      if (this.tooltipTrigger && this.tooltipTrigger.contains(target)) return;

      this.hideTooltip();
    }

    onTooltipKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') this.hideTooltip();
    }

    ensureTooltip() {
      if (this.tooltip) return this.tooltip;
      if (!this.tooltipTemplate || !this.tooltipTemplate.content.firstElementChild) return null;

      var self = this;
      var tooltip = this.tooltipTemplate.content.firstElementChild.cloneNode(true);
      tooltip.hidden = true;
      document.body.appendChild(tooltip);

      this.tooltip = tooltip;
      this.tooltipBody = tooltip.querySelector('[data-bb-reviews-tooltip-body]');

      /* The pointer may cross onto the tooltip to read (or scroll) a long review. */
      tooltip.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse') return;
        self.tooltipHovered = true;
        self.cancelTooltipHide();
        self.syncTooltipAutoplay();
      });

      tooltip.addEventListener('pointerleave', function (event) {
        if (event.pointerType !== 'mouse') return;
        self.tooltipHovered = false;
        self.scheduleTooltipHide();
      });

      return tooltip;
    }

    showTooltip(trigger, pinned) {
      var review = this.reviews[parseInt(trigger.getAttribute('data-bb-review-read-more'), 10)];
      var tooltip = review ? this.ensureTooltip() : null;
      if (!tooltip || !this.tooltipBody) return;

      this.cancelTooltipHide();

      var wasOpen = !tooltip.hidden;
      var sameTrigger = wasOpen && this.tooltipTrigger === trigger;

      if (!sameTrigger) {
        if (this.tooltipTrigger) this.tooltipTrigger.removeAttribute('aria-describedby');
        this.tooltipTrigger = trigger;
        this.tooltipBody.textContent = '';
        this.buildReviewDetails(this.tooltipBody, review, 'bb-reviews-tooltip', 'p');
        this.tooltipBody.scrollTop = 0;
      }

      /* Hover or focus on an already pinned tooltip never unpins it. */
      this.tooltipPinned = !!pinned || (sameTrigger && !!this.tooltipPinned);
      trigger.setAttribute('aria-describedby', tooltip.id);

      if (!wasOpen) {
        tooltip.hidden = false;
        document.addEventListener('pointerdown', this.handleTooltipOutsidePress, true);
        document.addEventListener('keydown', this.handleTooltipKeydown, true);
        window.addEventListener('scroll', this.handleTooltipReposition, true);
        window.addEventListener('resize', this.handleTooltipReposition);
      }

      this.positionTooltip();
      this.syncTooltipAutoplay();
    }

    /* Also slick's beforeChange handler, whose arguments it ignores. */
    hideTooltip() {
      this.cancelTooltipHide();
      if (!this.tooltip || this.tooltip.hidden) return;

      this.tooltip.hidden = true;
      if (this.tooltipTrigger) this.tooltipTrigger.removeAttribute('aria-describedby');

      this.tooltipTrigger = null;
      this.tooltipPinned = false;
      this.tooltipHovered = false;

      document.removeEventListener('pointerdown', this.handleTooltipOutsidePress, true);
      document.removeEventListener('keydown', this.handleTooltipKeydown, true);
      window.removeEventListener('scroll', this.handleTooltipReposition, true);
      window.removeEventListener('resize', this.handleTooltipReposition);

      if (this.tooltipFrame) {
        cancelAnimationFrame(this.tooltipFrame);
        this.tooltipFrame = null;
      }

      this.syncTooltipAutoplay();
    }

    scheduleTooltipHide() {
      if (this.tooltipPinned) return;
      this.cancelTooltipHide();
      this.tooltipHideTimer = setTimeout(this.hideTooltip.bind(this), TOOLTIP_HIDE_DELAY_MS);
    }

    cancelTooltipHide() {
      if (this.tooltipHideTimer) {
        clearTimeout(this.tooltipHideTimer);
        this.tooltipHideTimer = null;
      }
    }

    syncTooltipAutoplay() {
      if (!this.$slider || !this.autoplayRunning) return;

      var hold = !!(this.tooltipTrigger && (this.tooltipPinned || this.tooltipHovered));
      if (hold === !!this.tooltipHoldsAutoplay) return;
      this.tooltipHoldsAutoplay = hold;

      if (hold) {
        this.$slider.slick('slickPause');
      } else if (!this.userPaused) {
        this.$slider.slick('slickPlay');
      }
    }

    scheduleTooltipPosition() {
      if (this.tooltipFrame) return;
      var self = this;
      this.tooltipFrame = requestAnimationFrame(function () {
        self.tooltipFrame = null;
        self.positionTooltip();
      });
    }

    positionTooltip() {
      var tooltip = this.tooltip;
      var trigger = this.tooltipTrigger;
      if (!tooltip || tooltip.hidden || !trigger) return;

      var root = document.documentElement;
      var viewportWidth = root.clientWidth;
      var viewportHeight = root.clientHeight;
      var rect = trigger.getBoundingClientRect();

      /* Scrolled off screen, or its slide is gone: nothing left to point at. */
      if (!trigger.isConnected || rect.bottom < 0 || rect.top > viewportHeight || (!rect.width && !rect.height)) {
        this.hideTooltip();
        return;
      }

      /* Measure at the origin, free of the last position and height cap. */
      tooltip.style.left = '0px';
      tooltip.style.top = '0px';
      tooltip.style.maxHeight = '';

      var spaceAbove = rect.top - TOOLTIP_GAP - TOOLTIP_MARGIN;
      var spaceBelow = viewportHeight - rect.bottom - TOOLTIP_GAP - TOOLTIP_MARGIN;
      var height = tooltip.offsetHeight;
      var placement;

      if (height <= spaceAbove) {
        placement = 'top';
      } else if (height <= spaceBelow) {
        placement = 'bottom';
      } else {
        placement = spaceAbove >= spaceBelow ? 'top' : 'bottom';
        tooltip.style.maxHeight = Math.max(TOOLTIP_MIN_HEIGHT, placement === 'top' ? spaceAbove : spaceBelow) + 'px';
        height = tooltip.offsetHeight;
      }

      var width = tooltip.offsetWidth;
      var center = rect.left + rect.width / 2;
      var left = Math.max(TOOLTIP_MARGIN, Math.min(center - width / 2, viewportWidth - width - TOOLTIP_MARGIN));
      var top = placement === 'top' ? rect.top - TOOLTIP_GAP - height : rect.bottom + TOOLTIP_GAP;
      var arrowX = Math.max(TOOLTIP_ARROW_INSET, Math.min(center - left, width - TOOLTIP_ARROW_INSET));

      tooltip.setAttribute('data-placement', placement);
      tooltip.style.left = Math.round(left) + 'px';
      tooltip.style.top = Math.round(top) + 'px';
      tooltip.style.setProperty('--bb-tooltip-arrow-x', Math.round(arrowX) + 'px');
    }

    destroyTooltip() {
      this.hideTooltip();
      if (this.handleTooltipPointerEnd) this.onTooltipPointerEnd();
      if (this.tooltip && this.tooltip.parentNode) this.tooltip.parentNode.removeChild(this.tooltip);

      this.tooltip = null;
      this.tooltipBody = null;
    }

    formatNumber(value, decimals) {
      var number = Number(value);
      if (!isFinite(number)) return String(value);
      try {
        return number.toLocaleString(this.locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimals === undefined ? 1 : decimals
        });
      } catch (error) {
        return String(number);
      }
    }

    formatRelativeDate(value) {
      if (!value) return '';
      var date = new Date(value);
      if (isNaN(date.getTime())) return '';

      if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') {
        return this.formatDate(value);
      }

      var seconds = (date.getTime() - Date.now()) / 1000;

      try {
        var formatter = new Intl.RelativeTimeFormat(this.locale, { numeric: 'always' });

        for (var i = 0; i < RELATIVE_UNITS.length; i++) {
          var unit = RELATIVE_UNITS[i][0];
          var size = RELATIVE_UNITS[i][1];
          if (Math.abs(seconds) >= size) {
            return formatter.format(Math.round(seconds / size), unit);
          }
        }

        return formatter.format(Math.round(seconds), 'second');
      } catch (error) {
        return this.formatDate(value);
      }
    }

    formatDate(value) {
      if (!value) return '';
      var date = new Date(value);
      if (isNaN(date.getTime())) return '';
      try {
        return date.toLocaleDateString(this.locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        return date.toISOString().slice(0, 10);
      }
    }

    initCarousel() {
      var $ = jq();

      if (!this.showCarousel || !$ || this.items.length < 2) return;

      if (this.listEl.classList.contains('slick-initialized')) {
        $(this.listEl).slick('unslick');
      }

      if (this.prevEl) this.prevEl.hidden = false;
      if (this.nextEl) this.nextEl.hidden = false;

      var autoplay = this.rotating && this.autoplay && !prefersReducedMotion();
      this.autoplayRunning = autoplay;
      var dots = this.showDots && !!this.dotsEl;

      /* The row below the carousel only exists if something goes in it. */
      if (this.controlsEl) this.controlsEl.hidden = !(dots || autoplay);

      var settings = {
        infinite: this.infinite,
        arrows: !!(this.prevEl && this.nextEl),
        slidesToShow: this.itemsDesktop,
        slidesToScroll: 1,
        dots: dots,
        /* Theme breakpoints: slick applies a block while width < breakpoint. */
        responsive: [
          { breakpoint: 990, settings: { slidesToShow: this.itemsTablet } },
          { breakpoint: 750, settings: { slidesToShow: this.itemsMobile } }
        ]
      };

      if (this.prevEl) settings.prevArrow = $(this.prevEl);
      if (this.nextEl) settings.nextArrow = $(this.nextEl);
      if (dots) settings.appendDots = $(this.dotsEl);

      if (autoplay) {
        settings.autoplay = true;
        settings.autoplaySpeed = this.autoplaySpeed;
        settings.pauseOnHover = this.pauseOnHover;
      }

      this.$slider = $(this.listEl);
      this.$slider.slick(settings);

      /* Any slide change — arrow, dot, swipe or autoplay — moves the trigger away. */
      if (this.reviewDisplayType === 'tooltip') {
        this.$slider
          .off('beforeChange.bbReviewsTooltip')
          .on('beforeChange.bbReviewsTooltip', this.hideTooltip.bind(this));
      }

      if (autoplay) this.setUpPauseToggle();
    }

    setUpPauseToggle() {
      if (!this.toggleEl || !this.$slider) return;

      var self = this;
      this.toggleEl.hidden = false;

      /* One listener per element, however many times the carousel re-inits. */
      if (!this.toggleBound) {
        this.toggleBound = true;
        this.toggleEl.addEventListener('click', function () {
          self.userPaused = !self.userPaused;
          self.$slider.slick(self.userPaused ? 'slickPause' : 'slickPlay');
          self.syncToggle();
        });
      }

      this.syncToggle();
    }

    syncToggle() {
      if (!this.toggleEl) return;

      var pauseIcon = this.toggleEl.querySelector('.icon-pause');
      var playIcon = this.toggleEl.querySelector('.icon-play');

      if (pauseIcon) pauseIcon.hidden = this.userPaused;
      if (playIcon) playIcon.hidden = !this.userPaused;

      this.toggleEl.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      this.toggleEl.setAttribute(
        'aria-label',
        (this.userPaused ? this.strings.play : this.strings.pause) || ''
      );
    }
  }

  /*
    Shared with assets/bb-reviews-full.js, which extends this element, so the
    request cache, stars and scrolling stay in one place.
  */
  BBReviews.kit = {
    requestReviews: requestReviews,
    buildStars: buildStars,
    fill: fill,
    stickyHeaderOffset: stickyHeaderOffset,
    smoothScrollTo: smoothScrollTo,
    MAX_STARS: MAX_STARS
  };
  window.BBReviews = BBReviews;

  customElements.define('bb-reviews', BBReviews);

  /*
    snippets/bb-product-reviews.liquid's "write a review" fallback. It stays
    [hidden] until the shared request settles and is revealed only when
    data.reviews is an empty array — data.total plays no part, so
    { total: 2, reviews: [] } still shows it. Returned reviews, a failed
    request or a malformed payload all leave it hidden.
  */
  class BBReviewsWriteGate extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      var self = this;
      var endpoint = this.dataset.endpoint;
      if (!endpoint) return;

      requestReviews(endpoint)
        .then(function (payload) {
          var data = payload && payload.data;
          if (data && Array.isArray(data.reviews) && data.reviews.length === 0) {
            self.hidden = false;
          }
        })
        .catch(function () {});
    }
  }

  customElements.define('bb-reviews-write-gate', BBReviewsWriteGate);
})();
